import { useEffect, useSyncExternalStore } from 'react'

/**
 * The page's name in the top bar, after WonderWorks (Huanrui, 2026-10-19):
 * "_Internal Library", "_Upscale", a project's own title. Most pages are
 * named by where they are (TopNav reads the skills' names); a page whose name
 * is its content -- a project -- says it here with usePageName.
 */

let named = ''
const listeners = new Set<() => void>()

function set(next: string) {
  if (next === named) return
  named = next
  for (const listener of listeners) listener()
}

/** What a page has said its name is; '' when none has. */
export function usePageNameSaid(): string {
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener)
      return () => listeners.delete(listener)
    },
    () => named,
  )
}

/** Name this page in the top bar while it is on screen. */
export function usePageName(name: string) {
  useEffect(() => {
    set(name)
  }, [name])
  useEffect(() => () => set(''), [])
}

/* ---------- a place in the top bar for a page's own control (2026-10-21) ----------
   The project's title bar sits in the top bar beside WONDERWORKS: the bar
   gives the place (TopNav), the project page fills it with a portal. */

let slot: HTMLElement | null = null
const slotListeners = new Set<() => void>()

/** TopNav: the element a page may draw into, or null when it goes. */
export function setNavSlot(next: HTMLElement | null) {
  if (next === slot) return
  slot = next
  for (const listener of slotListeners) listener()
}

/** The top bar's place for this page's control; null until the bar is drawn. */
export function useNavSlot(): HTMLElement | null {
  return useSyncExternalStore(
    (listener) => {
      slotListeners.add(listener)
      return () => slotListeners.delete(listener)
    },
    () => slot,
  )
}
